import { Box, Paper, Stack, Typography } from "@mui/material";
import { useEffect } from "react";
import logo from "./CLlogo.jpg";
import LoginHero from "./LoginHero";
import { loginColors, loginType } from "./loginTheme";

export const fieldSx = {
  mb: "14px",
  "& .MuiInputLabel-root": { color: loginColors.muted },
  "& .MuiFormHelperText-root": { color: loginColors.muted },
  "& .MuiOutlinedInput-root": {
    height: 52,
    bgcolor: loginColors.input,
    borderRadius: "12px",
    "& .MuiOutlinedInput-input": {
      fontSize: loginType.input.fontSize,
      fontWeight: loginType.input.fontWeight,
    },
    "& fieldset": {
      borderColor: loginColors.border,
      borderWidth: 1,
    },
    "&:hover fieldset": { borderColor: "#D6DEE7" },
    "&.Mui-focused": {
      boxShadow: "0 0 0 3px rgba(12, 138, 128, 0.10)",
    },
    "&.Mui-focused fieldset": {
      borderColor: loginColors.teal,
      borderWidth: "1.5px",
    },
  },
};

export const iconSx = { color: loginColors.muted, fontSize: 22 };

export const submitButtonSx = {
  height: 52,
  bgcolor: loginColors.teal,
  color: "#fff",
  fontSize: loginType.button.fontSize,
  fontWeight: loginType.button.fontWeight,
  textTransform: "none",
  borderRadius: "12px",
  boxShadow: "0 8px 20px rgba(15, 69, 101, 0.10)",
  "&:hover": {
    bgcolor: loginColors.tealHover,
    transform: "translateY(-1px)",
    boxShadow: "0 12px 28px rgba(15, 69, 101, 0.16)",
  },
  "&.Mui-disabled": {
    bgcolor: "action.disabledBackground",
    color: "action.disabled",
    transform: "none",
    boxShadow: "none",
  },
};

export const ghostButtonSx = {
  color: "text.secondary",
  "&:hover": { bgcolor: "action.hover" },
};

const BrandLockup = () => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      mb: "16px",
      px: 1,
      fontFamily: loginType.fontFamily,
    }}
  >
    <Box
      component="img"
      src={logo}
      alt="Colombo Logistics"
      sx={{ height: 36, width: "auto", maxWidth: "100%", display: "block", mb: "10px" }}
    />
    <Typography
      component="p"
      sx={{
        ...loginType.systemTitle,
        fontFamily: "inherit",
        color: loginColors.text,
        textAlign: "center",
      }}
    >
      Procurement Management System
    </Typography>
  </Box>
);

const AuthSteps = ({ steps = [], activeStep = 0 }) => {
  if (!steps.length) return null;
  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="center"
      flexWrap="wrap"
      useFlexGap
      spacing={1.25}
      sx={{ mt: 3, rowGap: 1 }}
      aria-label="Sign-in steps"
    >
      {steps.map((label, index) => {
        const current = index === activeStep;
        const done = index < activeStep;
        const active = current || done;
        return (
          <Stack
            key={label}
            direction="row"
            alignItems="center"
            spacing={1.25}
            sx={{ minWidth: 0 }}
          >
            {index > 0 ? (
              <Box
                sx={{
                  width: 18,
                  height: 2,
                  bgcolor: "divider",
                  flexShrink: 0,
                }}
              />
            ) : null}
            <Stack direction="row" alignItems="center" spacing={0.75} sx={{ minWidth: 0 }}>
              <Box
                sx={{
                  width: 22,
                  height: 22,
                  borderRadius: "50%",
                  flexShrink: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  typography: "caption",
                  fontWeight: 700,
                  lineHeight: 1,
                  bgcolor: active ? "primary.main" : "action.selected",
                  color: active ? "primary.contrastText" : "text.secondary",
                }}
              >
                {index + 1}
              </Box>
              <Typography
                variant="caption"
                sx={{
                  fontWeight: current ? 700 : 500,
                  color: current ? "text.primary" : "text.secondary",
                  whiteSpace: "nowrap",
                }}
              >
                {label}
              </Typography>
            </Stack>
          </Stack>
        );
      })}
    </Stack>
  );
};

const CopyrightFooter = () => (
  <Typography
    component="footer"
    sx={{
      flexShrink: 0,
      width: "100%",
      pt: 2,
      textAlign: "center",
      fontSize: 12,
      lineHeight: 1.4,
      color: loginColors.muted,
    }}
  >
    © 2026 Colombo Logistics World (Pvt) Ltd.
  </Typography>
);

const AuthLayout = ({
  documentTitle,
  heading,
  helper,
  steps,
  activeStep = 0,
  children,
}) => {
  useEffect(() => {
    if (!documentTitle) return undefined;
    const previous = document.title;
    document.title = documentTitle;
    return () => {
      document.title = previous;
    };
  }, [documentTitle]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        width: "100%",
        height: "100vh",
        overflow: "hidden",
        bgcolor: loginColors.page,
        "@supports (height: 100dvh)": {
          height: "100dvh",
        },
      }}
    >
      <Box
        sx={{
          display: "block",
          position: "relative",
          flex: { xs: "0 0 auto", md: "0 0 52%", lg: "1 1 60%" },
          width: { xs: "100%", md: "52%", lg: "60%" },
          height: { xs: 200, md: "100%" },
          minWidth: 0,
          minHeight: 0,
        }}
      >
        <LoginHero />
      </Box>

      <Box
        sx={{
          position: "relative",
          zIndex: 1,
          flex: { xs: "1 1 auto", md: "0 0 48%", lg: "0 0 40%" },
          width: { xs: "100%", md: "48%", lg: "40%" },
          maxWidth: { lg: 600 },
          minWidth: 0,
          minHeight: 0,
          height: { md: "100%" },
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          ml: { md: "-48px" },
          pl: { xs: 2.5, sm: 4, md: "80px" },
          pr: { xs: 2.5, sm: 4 },
          py: { xs: 3, md: 5 },
          overflow: "hidden",
          overflowY: "auto",
          background: `linear-gradient(180deg, ${loginColors.panelTop} 0%, ${loginColors.panelBottom} 100%)`,
          maskImage: { md: "linear-gradient(to right, transparent, #000 48px)" },
          WebkitMaskImage: { md: "linear-gradient(to right, transparent, #000 48px)" },
        }}
      >
        <Box
          aria-hidden="true"
          sx={{
            position: "absolute",
            top: -120,
            right: -100,
            width: 360,
            height: 360,
            borderRadius: "50%",
            bgcolor: loginColors.teal,
            opacity: 0.015,
            pointerEvents: "none",
          }}
        />
        <Box
          sx={{
            width: "100%",
            maxWidth: 420,
            display: "flex",
            flexDirection: "column",
            flex: 1,
            justifyContent: "center",
          }}
        >
          <BrandLockup />
          <Paper
            elevation={0}
            sx={{
              width: "100%",
              p: "30px",
              border: "1px solid",
              borderColor: loginColors.cardBorder,
              borderRadius: "18px",
              bgcolor: loginColors.card,
              boxShadow: "0 12px 40px rgba(15, 69, 101, 0.08)",
            }}
          >
            <Typography
              component="h1"
              sx={{
                ...loginType.heading,
                fontFamily: loginType.fontFamily,
                mb: 0.75,
                color: loginColors.text,
              }}
            >
              {heading}
            </Typography>
            {helper ? (
              <Typography
                sx={{
                  ...loginType.supporting,
                  fontFamily: loginType.fontFamily,
                  mb: "24px",
                  color: loginColors.muted,
                }}
              >
                {helper}
              </Typography>
            ) : (
              <Box sx={{ mb: "24px" }} />
            )}
            {children}
            <AuthSteps steps={steps} activeStep={activeStep} />
          </Paper>
        </Box>
        <CopyrightFooter />
      </Box>
    </Box>
  );
};

export default AuthLayout;
